import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import {
  ControlDocsActivity,
  ControlDocsFile,
  ControlDocsFolder,
  ControlDocsService,
  ControlDocsTrashItems,
  ControlDocsWorkspaceItems,
  ControlDocsWorkspaceMode
} from '../../services/controldocs.service';
import { UserService } from '../../services/user.service';
import { UserSessionService } from '../../services/user-session.service';
import { API_BASE_URL } from '../../services/api-base';

@Component({
  selector: 'app-controldocs',
  templateUrl: './controldocs.component.html',
  styleUrls: ['./controldocs.component.scss']
})
export class ControlDocsComponent implements OnInit, OnDestroy {
  tenantId: number | null = null;
  userId: number | null = null;
  isTenantAdmin = false;

  folderForm: FormGroup;
  shareForm: FormGroup;
  uploading = false;
  uploadError = '';
  loadingFolders = false;
  loadingFiles = false;
  showNewFolderForm = false;
  searchTerm = '';
  viewMode: 'grid' | 'list' = 'list';
  activeSection: 'dashboard' | 'documents' | 'workspace' | 'shared' | 'trash' = 'dashboard';
  dropzoneDragOver = false;
  documentSort: { field: 'name' | 'type' | 'updated_at'; direction: 'asc' | 'desc' } = { field: 'updated_at', direction: 'desc' };
  columnWidths = {
    name: 340,
    type: 120,
    items: 90,
    updated: 180,
    owner: 220,
    visibility: 150,
    sharing: 170,
    trashMeta: 160,
    actions: 72
  };

  rootFolders: ControlDocsFolder[] = [];
  childFolders: ControlDocsFolder[] = [];
  files: ControlDocsFile[] = [];
  favoriteFolderItems: ControlDocsFolder[] = [];
  workspaceItems: Array<{ kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }> = [];
  sharedItems: Array<{ kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }> = [];
  trashItems: Array<{ kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }> = [];
  breadcrumb: ControlDocsFolder[] = [];
  currentFolder: ControlDocsFolder | null = null;
  apiBaseUrl = `${API_BASE_URL}`;
  productionUploadsBaseUrl = 'https://controlps.app';
  private folderIndex = new Map<number, ControlDocsFolder>();

  shareModalVisible = false;
  shareLink: string | null = null;
  sharePassword: string | null = null;
  shareLinkId: number | null = null;
  shareHasPassword = false;
  shareIsActive = true;
  shareCurrentPasswordValue: string | null = null;
  shareCurrentExpiresAt: string | null = null;
  shareGenerating = false;
  shareTarget: { type: 'FILE' | 'FOLDER'; id: number; name: string } | null = null;
  shareStep: 'form' | 'result' = 'form';
  actionMenuKey: string | null = null;
  actionMenuPosition: { top: number; left: number } | null = null;
  actionMenuRow: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile } | null = null;
  dashboardRecentFolders: ControlDocsFolder[] = [];
  dashboardRecentFiles: ControlDocsFile[] = [];
  dashboardActivities: ControlDocsActivity[] = [];
  previewModalVisible = false;
  previewTitle = '';
  previewTypeLabel = '';
  previewTypeIcon = 'insert_drive_file';
  previewKind: 'pdf' | 'image' | 'video' | 'audio' | 'unsupported' = 'unsupported';
  previewUrl: string | null = null;
  previewSafeUrl: SafeResourceUrl | null = null;
  previewDownloadUrl: string | null = null;
  private viewStateKey = 'controldocs:view-state';
  detailsModalVisible = false;
  detailsTab: 'details' | 'sharing' | 'external' = 'details';
  detailsTarget: { type: 'FILE' | 'FOLDER'; id: number; name: string; visibility: 'PUBLIC' | 'PRIVATE'; createdAt: string; createdBy: string } | null = null;
  detailsName = '';
  detailsVisibility: 'PUBLIC' | 'PRIVATE' = 'PRIVATE';
  detailsNameSaving = false;
  detailsVisibilitySaving = false;
  accessGrantsEmails: string[] = [];
  accessGrantsInput = '';
  accessGrantsLoading = false;
  accessGrantsSaving = false;
  tenantUsers: Array<{ id: number; name: string; email: string; active: number }> = [];
  usersLoading = false;
  accessGrantTags: Array<{ email: string; label: string }> = [];
  accessSearchTerm = '';
  accessSearchOpen = false;
  confirmModalVisible = false;
  confirmSubmitting = false;
  confirmAction: 'trash' | 'restore' | 'permanent_delete' = 'trash';
  confirmTarget: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile } | null = null;
  inlineRenameKey: string | null = null;
  inlineRenameValue = '';
  inlineRenameSaving = false;
  draggedFileId: number | null = null;
  folderDropTargetId: number | null = null;
  fileMoveInProgress = false;
  private resizingColumn: 'name' | 'type' | 'items' | 'updated' | 'owner' | 'visibility' | 'sharing' | 'trashMeta' | 'actions' | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeMoved = false;
  private suppressSortClickUntil = 0;

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private docs: ControlDocsService,
    private session: UserSessionService,
    private sanitizer: DomSanitizer,
    private userService: UserService
  ) {
    this.folderForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      visibility: ['PRIVATE', Validators.required]
    });
    this.shareForm = this.fb.group({
      password_enabled: [true],
      password: ['', Validators.required],
      expires_at: ['']
    });
  }

  generateStrongPassword(length = 12) {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i += 1) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.shareForm.patchValue({
      password_enabled: true,
      password
    });
  }

  ngOnInit() {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    this.isTenantAdmin = Number(user?.role_id || 0) === 1;
    if (this.tenantId && this.userId) {
      this.viewStateKey = `controldocs:view-state:${this.tenantId}:${this.userId}`;
    }
    if (this.tenantId && this.userId) {
      this.restoreViewState();
      this.loadRoot();
      this.loadFavoriteFolders();
      this.loadDashboardData();
      if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
        this.loadSectionItems();
      } else if (this.activeSection === 'trash') {
        this.loadTrashItems(this.currentFolder?.id || undefined);
      }
      if (this.currentFolder?.id) {
        if (this.activeSection === 'trash') {
          this.loadTrashItems(this.currentFolder.id);
        } else {
          this.loadChildFolders(this.currentFolder.id);
          this.loadFiles(this.currentFolder.id);
        }
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: MouseEvent) {
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.action-menu') || target?.closest('.action-dropdown-fixed')) {
      return;
    }
    this.closeActionMenu();
    this.accessSearchOpen = false;
    if (this.searchTerm && !target?.closest('.search-area')) {
      this.searchTerm = '';
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    if (!this.resizingColumn) {
      return;
    }
    if (Math.abs(event.clientX - this.resizeStartX) > 2) {
      this.resizeMoved = true;
    }
    const nextWidth = Math.max(72, this.resizeStartWidth + (event.clientX - this.resizeStartX));
    this.columnWidths = {
      ...this.columnWidths,
      [this.resizingColumn]: nextWidth
    };
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp() {
    if (this.resizingColumn) {
      this.suppressSortClickUntil = Date.now() + 180;
    }
    this.resizingColumn = null;
    this.resizeMoved = false;
  }

  toggleNewFolderForm() {
    this.showNewFolderForm = !this.showNewFolderForm;
    if (!this.showNewFolderForm) {
      this.folderForm.reset({ name: '', visibility: 'PRIVATE' });
    }
  }

  goToRoot() {
    this.currentFolder = null;
    this.breadcrumb = [];
    if (this.activeSection === 'trash') {
      this.loadTrashItems();
      this.files = [];
      this.childFolders = [];
    } else {
      this.loadRoot();
      this.files = [];
    }
    this.persistViewState();
  }

  openFolder(folder: ControlDocsFolder) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.searchTerm = '';
    this.currentFolder = folder;
    this.folderIndex.set(folder.id, folder);
    this.breadcrumb = this.buildBreadcrumb(folder);
    if (this.activeSection === 'trash') {
      this.loadTrashItems(folder.id);
    } else {
      this.loadChildFolders(folder.id);
      this.loadFiles(folder.id);
      this.logActivity('ACCESS_FOLDER', 'FOLDER', folder.id, folder.name, { parent_folder_id: folder.parent_folder_id || null });
    }
    this.persistViewState();
  }

  switchSection(section: 'dashboard' | 'documents' | 'workspace' | 'shared' | 'trash') {
    this.activeSection = section;
    if (section === 'workspace' || section === 'shared' || section === 'trash') {
      this.currentFolder = null;
      this.breadcrumb = [];
      this.viewMode = 'list';
      this.loadRoot();
      if (section === 'trash') {
        this.loadTrashItems();
      } else {
        this.loadSectionItems();
      }
      this.persistViewState();
      return;
    }
    if (section === 'documents') {
      this.viewMode = 'list';
    }
    this.persistViewState();
  }

  openFolderFromDashboard(folder: ControlDocsFolder) {
    this.switchSection('documents');
    this.openFolder(folder);
  }

  openFolderFromListing(folder: ControlDocsFolder) {
    if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
      this.switchSection('documents');
    }
    this.openFolder(folder);
  }

  openFromDocumentsList(folder: ControlDocsFolder, event?: Event) {
    event?.stopPropagation();
    this.openFolderFromListing(folder);
  }

  navigateTo(folder: ControlDocsFolder) {
    if (!folder) {
      return;
    }
    this.openFolder(folder);
  }

  navigateToById(folderId: number) {
    const id = Number(folderId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    const fromIndex = this.folderIndex.get(id);
    if (fromIndex) {
      this.openFolder(fromIndex);
      return;
    }
    const fromBreadcrumb = (this.breadcrumb || []).find((item) => Number(item?.id) === id);
    if (fromBreadcrumb) {
      this.openFolder(fromBreadcrumb);
    }
  }

  createFolder() {
    if (!this.folderForm.valid || !this.tenantId || !this.userId) {
      this.folderForm.markAllAsTouched();
      return;
    }

    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      name: this.folderForm.value.name,
      visibility: this.folderForm.value.visibility,
      parent_folder_id: this.currentFolder?.id || null
    };
    const sub = this.docs.createFolder(payload).subscribe({
      next: () => {
        this.toggleNewFolderForm();
        if (this.currentFolder) {
          this.loadChildFolders(this.currentFolder.id);
        } else {
          this.loadRoot();
        }
        this.loadDashboardData();
      }
    });
    this.subscriptions.push(sub);
  }

  async handleUpload(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files?.length) {
      return;
    }
    try {
      await this.uploadFilesToCurrentFolder(Array.from(files));
    } catch (error: any) {
      this.uploadError = error?.error?.error || 'Não foi possível enviar o arquivo.';
    } finally {
      if (input) {
        input.value = '';
      }
    }
  }

  async handleFolderUpload(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files?.length || !this.tenantId || !this.userId || !this.currentFolder) {
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    const folderId = this.currentFolder.id;
    const visibility = this.currentFolder.visibility || 'PRIVATE';
    const folderCache = new Map<string, number>();
    const loadedParents = new Set<number>();

    try {
      for (const file of Array.from(files)) {
        const relativePath = (file as any)?.webkitRelativePath || file.name;
        const segments = relativePath.split('/').filter(Boolean);
        const fileName = segments.pop();
        if (!fileName) {
          continue;
        }
        const targetFolderId = await this.ensureFolderPath(
          folderId,
          segments,
          visibility,
          folderCache,
          loadedParents
        );
        await firstValueFrom(
          this.docs.uploadFile({
            tenant_id: this.tenantId,
            uploaded_by: this.userId,
            folder_id: targetFolderId,
            file: new File([file], fileName, { type: file.type })
          })
        );
      }
      if (this.currentFolder?.id) {
        this.loadChildFolders(this.currentFolder.id);
        this.loadFiles(this.currentFolder.id);
      }
      this.loadDashboardData();
    } catch (error: any) {
      this.uploadError = error?.error?.error || 'Não foi possível enviar os arquivos.';
    } finally {
      this.uploading = false;
      if (input) {
        input.value = '';
      }
    }
  }

  private async ensureFolderPath(
    baseFolderId: number,
    segments: string[],
    visibility: 'PUBLIC' | 'PRIVATE',
    folderCache: Map<string, number>,
    loadedParents: Set<number>
  ) {
    let currentParentId = baseFolderId;
    for (const segment of segments) {
      const key = `${currentParentId}:${segment}`;
      const cached = folderCache.get(key);
      if (cached) {
        currentParentId = cached;
        continue;
      }

      if (!loadedParents.has(currentParentId)) {
        const folders = await firstValueFrom(this.docs.listFolders(this.tenantId!, this.userId!, currentParentId));
        folders.forEach((folder) => {
          folderCache.set(`${currentParentId}:${folder.name}`, folder.id);
        });
        loadedParents.add(currentParentId);
      }

      const existing = folderCache.get(key);
      if (existing) {
        currentParentId = existing;
        continue;
      }

      const created = await firstValueFrom(
        this.docs.createFolder({
          tenant_id: this.tenantId!,
          created_by: this.userId!,
          name: segment,
          visibility,
          parent_folder_id: currentParentId
        })
      );
      folderCache.set(key, created.id);
      currentParentId = created.id;
    }
    return currentParentId;
  }

  private loadRoot() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.loadingFolders = true;
    const sub = this.docs.listFolders(this.tenantId, this.userId).subscribe({
      next: (folders) => {
        this.rootFolders = folders;
        this.indexFolders(folders);
        if (!this.currentFolder) {
          this.childFolders = [];
        }
        if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
          this.loadSectionItems();
        } else if (this.activeSection === 'trash') {
          this.loadTrashItems();
        }
        this.loadingFolders = false;
      },
      error: () => (this.loadingFolders = false)
    });
    this.subscriptions.push(sub);
  }

  private loadChildFolders(parentId: number) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.loadingFolders = true;
    const sub = this.docs.listFolders(this.tenantId, this.userId, parentId).subscribe({
      next: (folders) => {
        this.childFolders = folders;
        this.indexFolders(folders);
        this.loadingFolders = false;
      },
      error: () => (this.loadingFolders = false)
    });
    this.subscriptions.push(sub);
  }

  private loadFiles(folderId: number) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.loadingFiles = true;
    const sub = this.docs.listFiles(this.tenantId, this.userId, folderId).subscribe({
      next: (files) => {
        this.files = files;
        this.loadingFiles = false;
      },
      error: () => (this.loadingFiles = false)
    });
    this.subscriptions.push(sub);
  }

  trackById(_index: number, item: { id: number }) {
    return item.id;
  }

  trackByDocumentRow(_index: number, row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? `folder-${row.folder.id}` : `file-${row.file.id}`;
  }

  get filteredFiles() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.files;
    }
    return this.files.filter((file) => file.name.toLowerCase().includes(term));
  }

  get filteredChildFolders() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.childFolders;
    }
    return this.childFolders.filter((folder) => folder.name.toLowerCase().includes(term));
  }

  get folderListScrollable() {
    return this.filteredChildFolders.length > 8;
  }

  get fileListScrollable() {
    return this.filteredFiles.length > 8;
  }

  get foldersForView() {
    const base = this.currentFolder ? this.filteredChildFolders : this.rootFolders;
    return base || [];
  }

  get recentFolders() {
    return (this.dashboardRecentFolders || []).slice(0, 4);
  }

  private get activeWorkspaceItems() {
    return this.activeSection === 'shared' ? this.sharedItems : this.workspaceItems;
  }

  get allFolders() {
    if (this.activeSection === 'trash') {
      const trashFolders = this.trashItems
        .filter((row): row is { kind: 'folder'; folder: ControlDocsFolder } => row.kind === 'folder')
        .map((row) => row.folder);
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        return trashFolders;
      }
      return trashFolders.filter((folder) => folder.name.toLowerCase().includes(term));
    }
    if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
      const workspaceFolders = this.activeWorkspaceItems
        .filter((row): row is { kind: 'folder'; folder: ControlDocsFolder } => row.kind === 'folder')
        .map((row) => row.folder);
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        return workspaceFolders;
      }
      return workspaceFolders.filter((folder) => folder.name.toLowerCase().includes(term));
    }
    return this.foldersForView;
  }

  get documentRows(): Array<{ kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }> {
    let rows: Array<{ kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }>;
    if (this.activeSection === 'trash') {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        rows = this.trashItems;
      } else {
        rows = this.trashItems.filter((row) => {
          const name = row.kind === 'folder' ? row.folder.name : row.file.name;
          return String(name || '').toLowerCase().includes(term);
        });
      }
    } else if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        rows = this.activeWorkspaceItems;
      } else {
        rows = this.activeWorkspaceItems.filter((row) => {
          const name = row.kind === 'folder' ? row.folder.name : row.file.name;
          return String(name || '').toLowerCase().includes(term);
        });
      }
    } else if (!this.currentFolder) {
      rows = this.allFolders.map((folder) => ({ kind: 'folder' as const, folder }));
    } else {
      rows = [
        ...this.filteredChildFolders.map((folder) => ({ kind: 'folder' as const, folder })),
        ...this.filteredFiles.map((file) => ({ kind: 'file' as const, file }))
      ];
    }
    return [...rows].sort((a, b) => this.compareDocumentRows(a, b));
  }

  get documentCount() {
    return this.documentRows.length;
  }

  get favoriteFolders() {
    return this.favoriteFolderItems || [];
  }

  get recentFiles() {
    return (this.dashboardRecentFiles || []).slice(0, 10);
  }

  toggleDocumentSort(field: 'name' | 'type' | 'updated_at') {
    if (this.documentSort.field === field) {
      this.documentSort = {
        field,
        direction: this.documentSort.direction === 'asc' ? 'desc' : 'asc'
      };
      return;
    }
    this.documentSort = { field, direction: field === 'updated_at' ? 'desc' : 'asc' };
  }

  sortIndicator(field: 'name' | 'type' | 'updated_at') {
    if (this.documentSort.field !== field) {
      return 'unfold_more';
    }
    return this.documentSort.direction === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  columnWidthStyle(column: 'name' | 'type' | 'items' | 'updated' | 'owner' | 'visibility' | 'sharing' | 'trashMeta' | 'actions') {
    return `${this.columnWidths[column]}px`;
  }

  onSortHeaderClick(field: 'name' | 'type' | 'updated_at', event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (Date.now() < this.suppressSortClickUntil) {
      return;
    }
    this.toggleDocumentSort(field);
  }

  startColumnResize(
    column: 'name' | 'type' | 'items' | 'updated' | 'owner' | 'visibility' | 'sharing' | 'trashMeta' | 'actions',
    event: MouseEvent
  ) {
    event.preventDefault();
    event.stopPropagation();
    this.resizingColumn = column;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.columnWidths[column];
    this.resizeMoved = false;
  }

  get recentFilesScrollable() {
    return this.recentFiles.length > 4;
  }

  get recentActivities() {
    return (this.dashboardActivities || []).slice(0, 20);
  }

  get activityListScrollable() {
    return this.recentActivities.length > 5;
  }

  get visibleBreadcrumb() {
    const items = this.breadcrumb || [];
    const maxItems = 3;
    return items.length <= maxItems ? items : items.slice(items.length - maxItems);
  }

  get hasBreadcrumbEllipsis() {
    return (this.breadcrumb || []).length > 3;
  }

  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = 'list';
    this.persistViewState();
  }

  isActionMenuOpen(key: string) {
    return this.actionMenuKey === key;
  }

  toggleActionMenu(key: string, event?: Event, row?: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    event?.stopPropagation();
    if (this.isActionMenuOpen(key)) {
      this.closeActionMenu();
      return;
    }
    const trigger = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.actionMenuPosition = trigger ? this.buildActionMenuPosition(trigger) : null;
    this.actionMenuKey = key;
    this.actionMenuRow = row || null;
  }

  closeActionMenu() {
    this.actionMenuKey = null;
    this.actionMenuPosition = null;
    this.actionMenuRow = null;
  }

  actionMenuStyle(key: string) {
    if (!this.isActionMenuOpen(key) || !this.actionMenuPosition) {
      return null;
    }
    return {
      top: `${this.actionMenuPosition.top}px`,
      left: `${this.actionMenuPosition.left}px`
    };
  }

  canManageSharedActions() {
    return this.activeSection !== 'shared' || this.isTenantAdmin;
  }

  onFolderAction(action: 'details' | 'share' | 'delete' | 'favorite', folder: ControlDocsFolder, event?: Event) {
    event?.stopPropagation();
    this.closeActionMenu();
    if ((action === 'share' || action === 'delete') && !this.canManageSharedActions()) {
      return;
    }
    if (action === 'details') {
      this.openDetailsModal('FOLDER', {
        id: folder.id,
        name: folder.name,
        visibility: folder.visibility,
        createdAt: folder.created_at,
        createdBy: folder.created_by_name || 'Usuário'
      });
      return;
    }
    if (action === 'share') {
      this.openShareModal('FOLDER', folder);
      return;
    }
    if (action === 'favorite') {
      this.toggleFolderFavorite(folder);
      return;
    }
    this.deleteFolder(folder);
  }

  onFileAction(action: 'details' | 'share' | 'delete', file: ControlDocsFile, event?: Event) {
    event?.stopPropagation();
    this.closeActionMenu();
    if ((action === 'share' || action === 'delete') && !this.canManageSharedActions()) {
      return;
    }
    if (action === 'details') {
      this.openDetailsModal('FILE', {
        id: file.id,
        name: file.name,
        visibility: file.visibility,
        createdAt: file.uploaded_at,
        createdBy: file.uploaded_by_name || 'Usuário'
      });
      return;
    }
    if (action === 'share') {
      this.openShareModal('FILE', file);
      return;
    }
    this.deleteFile(file);
  }

  deleteFolder(folder: ControlDocsFolder, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'folder', folder };
    this.confirmAction = 'trash';
    this.confirmModalVisible = true;
  }

  deleteFile(file: ControlDocsFile, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'file', file };
    this.confirmAction = 'trash';
    this.confirmModalVisible = true;
  }

  restoreFolder(folder: ControlDocsFolder, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'folder', folder };
    this.confirmAction = 'restore';
    this.confirmModalVisible = true;
  }

  restoreFile(file: ControlDocsFile, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'file', file };
    this.confirmAction = 'restore';
    this.confirmModalVisible = true;
  }

  permanentlyDeleteFolder(folder: ControlDocsFolder, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'folder', folder };
    this.confirmAction = 'permanent_delete';
    this.confirmModalVisible = true;
  }

  permanentlyDeleteFile(file: ControlDocsFile, event?: Event) {
    event?.stopPropagation();
    this.confirmTarget = { kind: 'file', file };
    this.confirmAction = 'permanent_delete';
    this.confirmModalVisible = true;
  }

  closeConfirmModal() {
    if (this.confirmSubmitting) {
      return;
    }
    this.confirmModalVisible = false;
    this.confirmAction = 'trash';
    this.confirmTarget = null;
  }

  confirmActionSubmit() {
    if (!this.confirmTarget || !this.tenantId || !this.userId || this.confirmSubmitting) {
      return;
    }
    this.confirmSubmitting = true;
    let request$: Observable<unknown>;
    if (this.confirmAction === 'restore') {
      request$ = this.confirmTarget.kind === 'folder'
        ? this.docs.restoreFolder(this.confirmTarget.folder.id, { tenant_id: this.tenantId, user_id: this.userId })
        : this.docs.restoreFile(this.confirmTarget.file.id, { tenant_id: this.tenantId, user_id: this.userId });
    } else if (this.confirmAction === 'permanent_delete') {
      request$ = this.confirmTarget.kind === 'folder'
        ? this.docs.deleteFolderPermanently(this.confirmTarget.folder.id, this.tenantId, this.userId)
        : this.docs.deleteFilePermanently(this.confirmTarget.file.id, this.tenantId, this.userId);
    } else {
      request$ = this.confirmTarget.kind === 'folder'
        ? this.docs.deleteFolder(this.confirmTarget.folder.id, this.tenantId, this.userId)
        : this.docs.deleteFile(this.confirmTarget.file.id, this.tenantId, this.userId);
    }
    const confirmedTarget = this.confirmTarget;
    const confirmedAction = this.confirmAction;

    const sub = request$.subscribe({
      next: () => {
        this.confirmSubmitting = false;
        this.confirmModalVisible = false;
        this.confirmTarget = null;
        this.loadTrashItems();
        this.loadFavoriteFolders();
        this.loadDashboardData();
        if (confirmedTarget.kind === 'folder' && this.currentFolder?.id === confirmedTarget.folder.id) {
          this.goToRoot();
        } else if (this.currentFolder?.id) {
          if (this.activeSection === 'trash') {
            this.loadTrashItems(this.currentFolder.id);
          } else {
            this.loadChildFolders(this.currentFolder.id);
            this.loadFiles(this.currentFolder.id);
          }
        } else {
          if (this.activeSection === 'trash') {
            this.loadTrashItems();
          } else {
            this.loadRoot();
          }
        }
        if (this.activeSection === 'workspace' || this.activeSection === 'shared') {
          this.loadSectionItems();
        } else if (this.activeSection === 'trash') {
          this.loadTrashItems(this.currentFolder?.id || undefined);
        }
      },
      error: (error: any) => {
        this.confirmSubmitting = false;
        const defaultMessage =
          confirmedAction === 'restore'
            ? 'Não foi possível restaurar o item.'
            : confirmedAction === 'permanent_delete'
              ? 'Não foi possível excluir permanentemente o item.'
              : 'Não foi possível mover o item para a lixeira.';
        const message = error?.error?.error || defaultMessage;
        alert(message);
      }
    });
    this.subscriptions.push(sub);
  }

  get confirmModalTitle() {
    if (!this.confirmTarget) {
      return '';
    }
    return this.confirmTarget.kind === 'folder' ? this.confirmTarget.folder.name : this.confirmTarget.file.name;
  }

  get confirmModalDescription() {
    if (!this.confirmTarget) {
      return '';
    }
    if (this.confirmAction === 'restore') {
      const ownerName = this.confirmTarget.kind === 'folder'
        ? (this.confirmTarget.folder.created_by_name || 'o proprietário original')
        : (this.confirmTarget.file.uploaded_by_name || 'o proprietário original');
      const parentName = this.confirmTarget.kind === 'folder'
        ? this.confirmTarget.folder.restore_parent_name
        : this.confirmTarget.file.restore_parent_name;
      return `O item será restaurado para ${parentName ? `"${parentName}"` : 'a Raiz'} de ${ownerName}.`;
    }
    if (this.confirmAction === 'permanent_delete') {
      return this.confirmTarget.kind === 'folder'
        ? 'A pasta sairá da sua lixeira e ficará disponível para restauração apenas pelo admin.'
        : 'O arquivo sairá da sua lixeira e ficará disponível para restauração apenas pelo admin.';
    }
    return this.confirmTarget.kind === 'folder'
      ? 'A pasta e todo o conteúdo dela serão enviados para a lixeira.'
      : 'O arquivo será enviado para a lixeira.';
  }

  get confirmModalHeading() {
    if (this.confirmAction === 'restore') {
      return 'Restaurar item';
    }
    if (this.confirmAction === 'permanent_delete') {
      return 'Excluir permanentemente';
    }
    return 'Mover para lixeira';
  }

  get confirmModalButtonLabel() {
    if (this.confirmSubmitting) {
      if (this.confirmAction === 'restore') {
        return 'Restaurando...';
      }
      if (this.confirmAction === 'permanent_delete') {
        return 'Excluindo...';
      }
      return 'Movendo...';
    }
    if (this.confirmAction === 'restore') {
      return 'Restaurar';
    }
    if (this.confirmAction === 'permanent_delete') {
      return 'Excluir permanentemente';
    }
    return 'Mover para lixeira';
  }

  openFile(file: ControlDocsFile, event?: Event) {
    this.openFilePreview(file, event);
  }

  openFilePreview(file: ControlDocsFile, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.logActivity('ACCESS_FILE', 'FILE', file.id, file.name, { folder_id: file.folder_id });
    const viewUrl = this.fileAccessUrl(file, 'view');
    const downloadUrl = this.fileAccessUrl(file, 'download');
    const title = this.normalizeDisplayText(file.name);
    const previewKind = this.previewKindFromTypeAndName(file.file_type, title);
    if (previewKind === 'pdf') {
      window.open(viewUrl, '_blank');
      return;
    }
    this.openPreviewModal(title, file.file_type, viewUrl, downloadUrl);
  }

  fileUrl(file: ControlDocsFile) {
    if (!file.file_path) {
      return null;
    }
    if (file.file_path.startsWith('http')) {
      return file.file_path;
    }
    return `${this.apiBaseUrl}${file.file_path}`;
  }

  private fileAccessUrl(file: ControlDocsFile, mode: 'view' | 'download') {
    if (mode === 'download' && this.tenantId && this.userId) {
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return `${this.productionUploadsBaseUrl}/api/controldocs/files/${file.id}/download?tenant_id=${this.tenantId}&user_id=${this.userId}`;
      }
      return this.docs.downloadUrl(file.id, this.tenantId, this.userId);
    }

    if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && file.file_path) {
      if (file.file_path.startsWith('http')) {
        return file.file_path;
      }
      if (file.file_path.startsWith('/uploads/')) {
        return `${this.productionUploadsBaseUrl}${file.file_path}`;
      }
    }
    return this.docs.viewUrl(file.id, this.tenantId || 0, this.userId || 0);
  }

  fileTypeIcon(file: ControlDocsFile): string {
    return this.iconFromTypeAndName(file?.file_type, file?.name);
  }

  onDropzoneDragOver(event: DragEvent) {
    if (!this.currentFolder) {
      return;
    }
    event.preventDefault();
    this.dropzoneDragOver = true;
  }

  onDropzoneDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dropzoneDragOver = false;
  }

  async onDropzoneDrop(event: DragEvent) {
    event.preventDefault();
    this.dropzoneDragOver = false;
    if (!this.currentFolder || !event.dataTransfer?.files?.length) {
      return;
    }
    await this.uploadFilesToCurrentFolder(Array.from(event.dataTransfer.files));
  }

  folderContentIcon(folder: ControlDocsFolder): string {
    if (folder?.latest_file_name || folder?.latest_file_type) {
      return this.iconFromTypeAndName(folder.latest_file_type, folder.latest_file_name);
    }
    return 'folder';
  }

  folderTypeLabel(folder: ControlDocsFolder): string {
    return 'Pasta';
  }

  folderDisplayName(folder: ControlDocsFolder): string {
    return this.normalizeDisplayText(folder.name);
  }

  fileDisplayName(file: ControlDocsFile): string {
    return this.normalizeDisplayText(file.name);
  }

  activityTargetName(item: ControlDocsActivity): string {
    return this.normalizeDisplayText(item?.target_name || 'item');
  }

  fileTypeLabel(file: ControlDocsFile): string {
    const icon = this.fileTypeIcon(file);
    switch (icon) {
      case 'picture_as_pdf':
        return 'PDF';
      case 'description':
        return 'Documento';
      case 'table_chart':
        return 'Planilha';
      case 'slideshow':
        return 'Apresentação';
      case 'image':
        return 'Imagem';
      case 'movie':
        return 'Vídeo';
      case 'audiotrack':
        return 'Áudio';
      case 'folder_zip':
        return 'Compactado';
      default:
        return file.file_type || 'Arquivo';
    }
  }

  onDocumentRowClick(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    if (row.kind === 'folder') {
      if (this.activeSection === 'trash') {
        event?.stopPropagation();
        this.openFolder(row.folder);
      } else {
        this.openFromDocumentsList(row.folder, event);
      }
      return;
    }
    this.openFilePreview(row.file, event);
  }

  documentRowIcon(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? 'folder' : this.fileTypeIcon(row.file);
  }

  documentRowName(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? this.folderDisplayName(row.folder) : this.fileDisplayName(row.file);
  }

  documentRowType(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? this.folderTypeLabel(row.folder) : this.fileTypeLabel(row.file);
  }

  documentRowVisibility(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? row.folder.visibility : row.file.visibility;
  }

  documentRowItemCount(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? row.folder.item_count || 0 : '-';
  }

  documentRowUpdatedAt(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    if (this.activeSection === 'trash') {
      return row.kind === 'folder' ? row.folder.deleted_at : row.file.deleted_at;
    }
    return row.kind === 'folder' ? row.folder.created_at : row.file.uploaded_at;
  }

  documentRowOwner(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? (row.folder.created_by_name || 'Usuário') : (row.file.uploaded_by_name || 'Usuário');
  }

  documentRowDaysRemaining(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    if (this.activeSection !== 'trash') {
      return '';
    }
    const adminOnlyAt = row.kind === 'folder' ? row.folder.admin_only_at : row.file.admin_only_at;
    if (adminOnlyAt) {
      return this.isTenantAdmin ? 'Somente admin' : 'Expirado';
    }
    const restoreUntil = this.documentRowRestoreDeadline(row);
    if (!restoreUntil) {
      return '-';
    }
    const now = Date.now();
    const diffMs = restoreUntil.getTime() - now;
    if (diffMs <= 0) {
      return this.isTenantAdmin ? 'Somente admin' : 'Expirado';
    }
    const totalHours = Math.ceil(diffMs / (1000 * 60 * 60));
    if (totalHours < 24) {
      return `${totalHours}h`;
    }
    const totalDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return `${totalDays} dias`;
  }

  private documentRowRestoreDeadline(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    const restoreUntilRaw = row.kind === 'folder'
      ? row.folder.restore_available_until
      : row.file.restore_available_until;
    if (restoreUntilRaw) {
      return new Date(restoreUntilRaw);
    }
    const deletedAtRaw = row.kind === 'folder' ? row.folder.deleted_at : row.file.deleted_at;
    if (!deletedAtRaw) {
      return null;
    }
    const deadline = new Date(deletedAtRaw);
    deadline.setDate(deadline.getDate() + 30);
    return deadline;
  }

  documentRowMenuKey(prefix: string, row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return `${prefix}-${row.kind}-${row.kind === 'folder' ? row.folder.id : row.file.id}`;
  }

  private buildActionMenuPosition(trigger: HTMLElement) {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 248;
    const viewportMargin = 12;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= menuHeight || rect.top < menuHeight
      ? Math.min(window.innerHeight - menuHeight - viewportMargin, rect.bottom + 8)
      : Math.max(viewportMargin, rect.top - menuHeight - 8);
    const left = Math.min(
      window.innerWidth - menuWidth - viewportMargin,
      Math.max(viewportMargin, rect.right - menuWidth)
    );

    return { top, left };
  }

  isInlineRenaming(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return this.inlineRenameKey === this.documentRowMenuKey('rename', row);
  }

  private compareDocumentRows(
    a: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile },
    b: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }
  ) {
    const direction = this.documentSort.direction === 'asc' ? 1 : -1;

    if (this.documentSort.field === 'name') {
      const aName = this.documentRowName(a).toLocaleLowerCase();
      const bName = this.documentRowName(b).toLocaleLowerCase();
      return aName.localeCompare(bName, 'pt-BR') * direction;
    }

    if (this.documentSort.field === 'type') {
      const aType = this.documentRowType(a).toLocaleLowerCase();
      const bType = this.documentRowType(b).toLocaleLowerCase();
      const typeCompare = aType.localeCompare(bType, 'pt-BR') * direction;
      if (typeCompare !== 0) {
        return typeCompare;
      }
      const aName = this.documentRowName(a).toLocaleLowerCase();
      const bName = this.documentRowName(b).toLocaleLowerCase();
      return aName.localeCompare(bName, 'pt-BR') * direction;
    }

    const aTime = this.documentRowUpdatedAt(a) ? new Date(this.documentRowUpdatedAt(a) as string).getTime() : 0;
    const bTime = this.documentRowUpdatedAt(b) ? new Date(this.documentRowUpdatedAt(b) as string).getTime() : 0;
    return (aTime - bTime) * direction;
  }

  isDocumentRowDraggable(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return this.activeSection === 'documents' && row.kind === 'file' && !!this.currentFolder && !this.fileMoveInProgress;
  }

  isFolderDropTarget(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' && this.folderDropTargetId === row.folder.id;
  }

  onDocumentRowDragStart(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event: DragEvent) {
    if (!this.isDocumentRowDraggable(row) || !event.dataTransfer) {
      event.preventDefault();
      return;
    }
    if (row.kind !== 'file') {
      event.preventDefault();
      return;
    }
    this.draggedFileId = row.file.id;
    this.folderDropTargetId = null;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(row.file.id));
    event.dataTransfer.setData('application/x-controldocs-file', String(row.file.id));
  }

  onDocumentRowDragEnd() {
    this.draggedFileId = null;
    this.folderDropTargetId = null;
  }

  onFolderRowDragOver(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event: DragEvent) {
    if (row.kind !== 'folder' || !this.draggedFileId) {
      return;
    }
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.folderDropTargetId = row.folder.id;
  }

  onFolderRowDragLeave(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event: DragEvent) {
    if (row.kind !== 'folder' || this.folderDropTargetId !== row.folder.id) {
      return;
    }
    const currentTarget = event.currentTarget as HTMLElement | null;
    const relatedTarget = event.relatedTarget as Node | null;
    if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }
    this.folderDropTargetId = null;
  }

  onFolderRowDrop(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event: DragEvent) {
    if (row.kind !== 'folder') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const fileId = this.draggedFileId || Number(event.dataTransfer?.getData('application/x-controldocs-file') || 0);
    this.draggedFileId = null;
    this.folderDropTargetId = null;
    if (!fileId || !this.tenantId || !this.userId || this.fileMoveInProgress) {
      return;
    }
    this.moveFileToFolder(fileId, row.folder);
  }

  private moveFileToFolder(fileId: number, folder: ControlDocsFolder) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.fileMoveInProgress = true;
    const sub = this.docs.moveFile(fileId, {
      tenant_id: this.tenantId,
      user_id: this.userId,
      target_folder_id: folder.id
    }).subscribe({
      next: () => {
        this.fileMoveInProgress = false;
        if (this.currentFolder?.id) {
          this.loadChildFolders(this.currentFolder.id);
          this.loadFiles(this.currentFolder.id);
        } else {
          this.loadRoot();
        }
        this.loadDashboardData();
      },
      error: (error: any) => {
        this.fileMoveInProgress = false;
        alert(error?.error?.error || 'Não foi possível mover o arquivo para a pasta.');
      }
    });
    this.subscriptions.push(sub);
  }

  isDocumentRowFavorite(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    return row.kind === 'folder' ? this.isFavorite(row.folder) : false;
  }

  onDocumentRowShare(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    if (row.kind === 'folder') {
      this.onFolderAction('share', row.folder, event);
      return;
    }
    this.onFileAction('share', row.file, event);
  }

  onDocumentRowDelete(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    if (row.kind === 'folder') {
      this.onFolderAction('delete', row.folder, event);
      return;
    }
    this.onFileAction('delete', row.file, event);
  }

  onDocumentRowFavorite(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    if (row.kind !== 'folder') {
      return;
    }
    this.onFolderAction('favorite', row.folder, event);
  }

  onDocumentRowDetails(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    if (row.kind === 'folder') {
      this.onFolderAction('details', row.folder, event);
      return;
    }
    this.onFileAction('details', row.file, event);
  }

  documentRowHasShareLink(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    const value = row.kind === 'folder' ? row.folder.has_share_link : row.file.has_share_link;
    return Number(value || 0) === 1 || value === true;
  }

  canShowRowQuickActions(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }) {
    if (this.activeSection === 'trash') {
      return false;
    }
    if (this.isInlineRenaming(row)) {
      return false;
    }
    if (row.kind === 'folder') {
      return true;
    }
    return true;
  }

  onDocumentRowQuickOpen(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    event?.stopPropagation();
    this.onDocumentRowClick(row, event);
  }

  onDocumentRowQuickDownload(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    event?.stopPropagation();
    if (row.kind !== 'file' || !this.tenantId || !this.userId) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = this.fileAccessUrl(row.file, 'download');
    anchor.setAttribute('download', row.file.name || 'arquivo');
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  startInlineRename(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    event?.stopPropagation();
    this.closeActionMenu();
    this.inlineRenameKey = this.documentRowMenuKey('rename', row);
    this.inlineRenameValue = this.documentRowName(row);
  }

  cancelInlineRename(event?: Event) {
    event?.stopPropagation();
    if (this.inlineRenameSaving) {
      return;
    }
    this.inlineRenameKey = null;
    this.inlineRenameValue = '';
  }

  saveInlineRename(row: { kind: 'folder'; folder: ControlDocsFolder } | { kind: 'file'; file: ControlDocsFile }, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId || this.inlineRenameSaving) {
      return;
    }
    const nextName = this.normalizeDisplayText(String(this.inlineRenameValue || '').trim());
    const currentName = this.documentRowName(row);
    if (nextName.length < 2) {
      alert('Informe um nome válido com pelo menos 2 caracteres.');
      return;
    }
    if (nextName === currentName) {
      this.cancelInlineRename();
      return;
    }

    this.inlineRenameSaving = true;
    const request$ = row.kind === 'folder'
      ? this.docs.renameFolder(row.folder.id, { tenant_id: this.tenantId, user_id: this.userId, name: nextName })
      : this.docs.renameFile(row.file.id, { tenant_id: this.tenantId, user_id: this.userId, name: nextName });

    const targetType = row.kind;
    const targetId = row.kind === 'folder' ? row.folder.id : row.file.id;
    const sub = request$.subscribe({
      next: () => {
        this.inlineRenameSaving = false;
        this.syncTargetName(targetType === 'folder' ? 'FOLDER' : 'FILE', targetId, nextName);
        if (this.detailsTarget?.id === targetId && this.detailsTarget?.type === (targetType === 'folder' ? 'FOLDER' : 'FILE')) {
          this.detailsTarget = { ...this.detailsTarget, name: nextName };
          this.detailsName = nextName;
        }
        this.inlineRenameKey = null;
        this.inlineRenameValue = '';
      },
      error: (error: any) => {
        this.inlineRenameSaving = false;
        alert(error?.error?.error || 'Não foi possível renomear o item.');
      }
    });
    this.subscriptions.push(sub);
  }

  openPreviewModal(title: string, fileType: string, viewUrl: string | null, downloadUrl: string | null) {
    this.previewTitle = title;
    this.previewUrl = viewUrl;
    this.previewDownloadUrl = downloadUrl;
    const detectedIcon = this.iconFromTypeAndName(fileType, title);
    this.previewTypeIcon = detectedIcon;
    this.previewTypeLabel = this.fileTypeLabel({
      id: 0,
      tenant_id: 0,
      folder_id: 0,
      name: title,
      file_type: fileType || '',
      file_size: 0,
      file_path: '',
      visibility: 'PRIVATE',
      uploaded_at: ''
    });
    this.previewKind = this.previewKindFromTypeAndName(fileType, title);
    this.previewSafeUrl =
      this.previewKind === 'pdf' && this.previewUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl) : null;
    this.previewModalVisible = true;
  }

  closePreviewModal() {
    this.previewModalVisible = false;
    this.previewTitle = '';
    this.previewTypeLabel = '';
    this.previewTypeIcon = 'insert_drive_file';
    this.previewKind = 'unsupported';
    this.previewUrl = null;
    this.previewSafeUrl = null;
    this.previewDownloadUrl = null;
  }

  openPreviewInNewTab() {
    if (this.previewUrl) {
      window.open(this.previewUrl, '_blank');
      return;
    }
    if (this.previewDownloadUrl) {
      window.open(this.previewDownloadUrl, '_blank');
    }
  }

  downloadPreviewFile() {
    if (this.previewDownloadUrl) {
      window.open(this.previewDownloadUrl, '_blank');
    }
  }

  private normalizeDisplayText(value: string | null | undefined): string {
    const text = String(value || '');
    if (!text) {
      return '';
    }
    const likelyMojibake = /Ã.|Â|â|�|€/.test(text);
    if (!likelyMojibake) {
      return text;
    }
    try {
      // Recovers strings incorrectly decoded as latin1 that should be utf-8.
      const decoded = decodeURIComponent(escape(text));
      if (decoded && decoded !== text) {
        return decoded;
      }
    } catch {
      // fallback map below
    }
    const replacements: Array<[RegExp, string]> = [
      [/Ã¡/g, 'á'],
      [/Ã /g, 'à'],
      [/Ã¢/g, 'â'],
      [/Ã£/g, 'ã'],
      [/Ã¤/g, 'ä'],
      [/Ã©/g, 'é'],
      [/Ã¨/g, 'è'],
      [/Ãª/g, 'ê'],
      [/Ã«/g, 'ë'],
      [/Ã­/g, 'í'],
      [/Ã¬/g, 'ì'],
      [/Ã®/g, 'î'],
      [/Ã¯/g, 'ï'],
      [/Ã³/g, 'ó'],
      [/Ã²/g, 'ò'],
      [/Ã´/g, 'ô'],
      [/Ãµ/g, 'õ'],
      [/Ã¶/g, 'ö'],
      [/Ãº/g, 'ú'],
      [/Ã¹/g, 'ù'],
      [/Ã»/g, 'û'],
      [/Ã¼/g, 'ü'],
      [/Ã§/g, 'ç'],
      [/Ã±/g, 'ñ'],
      [/Ã/g, 'Á'],
      [/Ã€/g, 'À'],
      [/Ã‚/g, 'Â'],
      [/Ãƒ/g, 'Ã'],
      [/Ã„/g, 'Ä'],
      [/Ã‰/g, 'É'],
      [/Ãˆ/g, 'È'],
      [/ÃŠ/g, 'Ê'],
      [/Ã‹/g, 'Ë'],
      [/Ã/g, 'Í'],
      [/ÃŒ/g, 'Ì'],
      [/ÃŽ/g, 'Î'],
      [/Ã/g, 'Ï'],
      [/Ã“/g, 'Ó'],
      [/Ã’/g, 'Ò'],
      [/Ã”/g, 'Ô'],
      [/Ã•/g, 'Õ'],
      [/Ã–/g, 'Ö'],
      [/Ãš/g, 'Ú'],
      [/Ã™/g, 'Ù'],
      [/Ã›/g, 'Û'],
      [/Ãœ/g, 'Ü'],
      [/Ã‡/g, 'Ç'],
      [/Ã‘/g, 'Ñ'],
      [/â€“/g, '–'],
      [/â€”/g, '—'],
      [/â€˜/g, '‘'],
      [/â€™/g, '’'],
      [/â€œ/g, '“'],
      [/â€\x9d/g, '”'],
      [/â€¢/g, '•'],
      [/â€¦/g, '…'],
      [/€"/g, '”'],
      [/€˜/g, '‘'],
      [/€™/g, '’'],
      [/€/g, 'ê']
    ];
    let fixed = text;
    for (const [pattern, replacement] of replacements) {
      fixed = fixed.replace(pattern, replacement);
    }
    return fixed;
  }

  private async uploadFilesToCurrentFolder(files: File[]) {
    if (!files?.length || !this.tenantId || !this.userId || !this.currentFolder) {
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    const folderId = this.currentFolder.id;
    try {
      for (const file of files) {
        await firstValueFrom(
          this.docs.uploadFile({
            tenant_id: this.tenantId,
            uploaded_by: this.userId,
            folder_id: folderId,
            file
          })
        );
      }
      this.loadChildFolders(folderId);
      this.loadFiles(folderId);
      this.loadDashboardData();
    } finally {
      this.uploading = false;
    }
  }

  isFavorite(folder: ControlDocsFolder): boolean {
    return Number(folder?.is_favorite || 0) === 1 || folder?.is_favorite === true;
  }

  toggleFolderFavorite(folder: ControlDocsFolder) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const nextFavorite = !this.isFavorite(folder);
    const updateFavorite = (arr: ControlDocsFolder[]) =>
      arr.map((item) => (item.id === folder.id ? { ...item, is_favorite: nextFavorite ? 1 : 0 } : item));

    // Optimistic update so Favorites sidebar updates immediately.
    this.rootFolders = updateFavorite(this.rootFolders);
    this.childFolders = updateFavorite(this.childFolders);
    this.dashboardRecentFolders = updateFavorite(this.dashboardRecentFolders);
    this.favoriteFolderItems = nextFavorite
      ? [{ ...folder, is_favorite: 1 }, ...this.favoriteFolderItems.filter((item) => item.id !== folder.id)]
      : this.favoriteFolderItems.filter((item) => item.id !== folder.id);
    this.workspaceItems = this.workspaceItems.map((row) =>
      row.kind === 'folder' && row.folder.id === folder.id ? { kind: 'folder', folder: { ...row.folder, is_favorite: nextFavorite ? 1 : 0 } } : row
    );
    this.sharedItems = this.sharedItems.map((row) =>
      row.kind === 'folder' && row.folder.id === folder.id ? { kind: 'folder', folder: { ...row.folder, is_favorite: nextFavorite ? 1 : 0 } } : row
    );

    const sub = this.docs
      .setFolderFavorite(folder.id, {
        tenant_id: this.tenantId,
        user_id: this.userId,
        is_favorite: nextFavorite
      })
      .subscribe({
        next: () => {
          this.loadFavoriteFolders();
          this.loadDashboardData();
        },
        error: () => {
          // rollback in case backend rejects or route is unavailable
          this.rootFolders = this.rootFolders.map((item) =>
            item.id === folder.id ? { ...item, is_favorite: nextFavorite ? 0 : 1 } : item
          );
          this.childFolders = this.childFolders.map((item) =>
            item.id === folder.id ? { ...item, is_favorite: nextFavorite ? 0 : 1 } : item
          );
          this.dashboardRecentFolders = this.dashboardRecentFolders.map((item) =>
            item.id === folder.id ? { ...item, is_favorite: nextFavorite ? 0 : 1 } : item
          );
          this.favoriteFolderItems = nextFavorite
            ? this.favoriteFolderItems.filter((item) => item.id !== folder.id)
            : [{ ...folder, is_favorite: 1 }, ...this.favoriteFolderItems.filter((item) => item.id !== folder.id)];
          this.workspaceItems = this.workspaceItems.map((row) =>
            row.kind === 'folder' && row.folder.id === folder.id
              ? { kind: 'folder', folder: { ...row.folder, is_favorite: nextFavorite ? 0 : 1 } }
              : row
          );
          this.sharedItems = this.sharedItems.map((row) =>
            row.kind === 'folder' && row.folder.id === folder.id
              ? { kind: 'folder', folder: { ...row.folder, is_favorite: nextFavorite ? 0 : 1 } }
              : row
          );
        }
      });
    this.subscriptions.push(sub);
  }

  private iconFromTypeAndName(fileType?: string | null, fileName?: string | null): string {
    const name = String(fileName || '').toLowerCase();
    const type = String(fileType || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() || '' : '';
    const hint = `${type} ${ext}`;

    if (/(pdf)/.test(hint)) return 'picture_as_pdf';
    if (/(doc|docx|odt|rtf|text|txt)/.test(hint)) return 'description';
    if (/(xls|xlsx|csv|ods)/.test(hint)) return 'table_chart';
    if (/(ppt|pptx|odp)/.test(hint)) return 'slideshow';
    if (/(jpg|jpeg|png|gif|bmp|webp|svg|image)/.test(hint)) return 'image';
    if (/(mp4|avi|mov|mkv|webm|video)/.test(hint)) return 'movie';
    if (/(mp3|wav|ogg|aac|audio)/.test(hint)) return 'audiotrack';
    if (/(zip|rar|7z|tar|gz)/.test(hint)) return 'folder_zip';
    if (/(json|xml|yaml|yml|log|md)/.test(hint)) return 'article';
    return 'insert_drive_file';
  }

  private previewKindFromTypeAndName(fileType?: string | null, fileName?: string | null) {
    const icon = this.iconFromTypeAndName(fileType, fileName);
    if (icon === 'picture_as_pdf') return 'pdf';
    if (icon === 'image') return 'image';
    if (icon === 'movie') return 'video';
    if (icon === 'audiotrack') return 'audio';
    return 'unsupported';
  }


  private indexFolders(folders: ControlDocsFolder[]) {
    folders.forEach((folder) => {
      this.folderIndex.set(folder.id, folder);
    });
  }

  private buildBreadcrumb(folder: ControlDocsFolder) {
    const path: ControlDocsFolder[] = [];
    const seen = new Set<number>();
    let current: ControlDocsFolder | undefined = folder;
    while (current && !seen.has(current.id)) {
      path.push(current);
      seen.add(current.id);
      if (!current.parent_folder_id) {
        break;
      }
      current = this.folderIndex.get(current.parent_folder_id);
    }
    return path.reverse();
  }

  openShareModal(type: 'FILE' | 'FOLDER', target: { id: number; name: string }, event?: Event) {
    event?.stopPropagation();
    if (!this.canManageSharedActions()) {
      return;
    }
    this.shareTarget = { type, id: target.id, name: target.name };
    this.shareForm.reset({
      password_enabled: true,
      password: '',
      expires_at: ''
    });
    this.shareLinkId = null;
    this.shareHasPassword = false;
    this.shareIsActive = true;
    this.shareCurrentPasswordValue = null;
    this.shareCurrentExpiresAt = null;
    this.shareLink = null;
    this.sharePassword = null;
    this.shareStep = 'form';
    this.shareModalVisible = true;
    this.loadShareDataForTarget(type, target.id);
  }

  openDetailsModal(
    type: 'FILE' | 'FOLDER',
    target: { id: number; name: string; visibility: 'PUBLIC' | 'PRIVATE'; createdAt: string; createdBy: string }
  ) {
    this.detailsTarget = {
      type,
      id: target.id,
      name: target.name,
      visibility: target.visibility,
      createdAt: target.createdAt,
      createdBy: target.createdBy
    };
    this.detailsName = target.name;
    this.detailsVisibility = target.visibility;
    this.detailsTab = 'details';
    this.detailsModalVisible = true;
    this.shareTarget = { type, id: target.id, name: target.name };
    this.shareForm.reset({ password_enabled: true, password: '', expires_at: '' });
    this.shareStep = 'form';
    this.ensureTenantUsersLoaded();
    this.loadShareDataForTarget(type, target.id);
    this.loadAccessGrants();
  }

  closeDetailsModal() {
    this.detailsModalVisible = false;
    this.detailsTarget = null;
    this.detailsName = '';
    this.detailsNameSaving = false;
    this.detailsVisibility = 'PRIVATE';
    this.detailsVisibilitySaving = false;
    this.accessGrantsEmails = [];
    this.accessGrantsInput = '';
    this.accessGrantTags = [];
    this.accessSearchTerm = '';
    this.accessSearchOpen = false;
  }

  saveDetailsVisibility() {
    if (!this.tenantId || !this.userId || !this.detailsTarget || this.detailsVisibilitySaving) {
      return;
    }
    const nextVisibility = this.detailsVisibility;
    const currentType = this.detailsTarget.type;
    const currentId = this.detailsTarget.id;
    this.detailsVisibilitySaving = true;

    const request$ =
      currentType === 'FOLDER'
        ? this.docs.updateFolderVisibility(currentId, {
            tenant_id: this.tenantId,
            user_id: this.userId,
            visibility: nextVisibility
          })
        : this.docs.updateFileVisibility(currentId, {
            tenant_id: this.tenantId,
            user_id: this.userId,
            visibility: nextVisibility
          });

    const sub = request$.subscribe({
      next: () => {
        this.detailsVisibilitySaving = false;
        if (this.detailsTarget) {
          this.detailsTarget = { ...this.detailsTarget, visibility: nextVisibility };
        }
        if (nextVisibility === 'PUBLIC') {
          this.ensureTenantUsersLoaded();
          this.loadAccessGrants();
        } else {
          this.accessGrantsEmails = [];
          this.accessGrantsInput = '';
          this.accessGrantTags = [];
          this.accessSearchTerm = '';
          this.accessSearchOpen = false;
        }
        this.syncTargetVisibility(currentType, currentId, nextVisibility);
        if (this.currentFolder?.id) {
          this.loadChildFolders(this.currentFolder.id);
          this.loadFiles(this.currentFolder.id);
        } else {
          this.loadRoot();
        }
        this.loadDashboardData();
      },
      error: () => {
        this.detailsVisibilitySaving = false;
        this.detailsVisibility = this.detailsTarget?.visibility || 'PRIVATE';
        alert('Não foi possível atualizar a visibilidade.');
      }
    });
    this.subscriptions.push(sub);
  }

  get canRenameDetailsTarget() {
    return !!this.detailsTarget && this.canManageSharedActions();
  }

  saveDetailsName() {
    if (!this.tenantId || !this.userId || !this.detailsTarget || this.detailsNameSaving) {
      return;
    }

    const nextName = this.normalizeDisplayText(String(this.detailsName || '').trim());
    if (nextName.length < 2) {
      alert('Informe um nome válido com pelo menos 2 caracteres.');
      return;
    }
    if (nextName === this.detailsTarget.name) {
      return;
    }

    this.detailsNameSaving = true;
    const currentType = this.detailsTarget.type;
    const currentId = this.detailsTarget.id;
    const previousName = this.detailsTarget.name;
    const request$ =
      currentType === 'FOLDER'
        ? this.docs.renameFolder(currentId, {
            tenant_id: this.tenantId,
            user_id: this.userId,
            name: nextName
          })
        : this.docs.renameFile(currentId, {
            tenant_id: this.tenantId,
            user_id: this.userId,
            name: nextName
          });

    const sub = request$.subscribe({
      next: () => {
        this.detailsNameSaving = false;
        if (this.detailsTarget) {
          this.detailsTarget = { ...this.detailsTarget, name: nextName };
        }
        this.detailsName = nextName;
        this.syncTargetName(currentType, currentId, nextName);
      },
      error: (error: any) => {
        this.detailsNameSaving = false;
        this.detailsName = previousName;
        alert(error?.error?.error || 'Não foi possível renomear o item.');
      }
    });
    this.subscriptions.push(sub);
  }

  private syncTargetVisibility(type: 'FILE' | 'FOLDER', id: number, visibility: 'PUBLIC' | 'PRIVATE') {
    if (type === 'FOLDER') {
      this.rootFolders = this.rootFolders.map((item) => (item.id === id ? { ...item, visibility } : item));
      this.childFolders = this.childFolders.map((item) => (item.id === id ? { ...item, visibility } : item));
      this.dashboardRecentFolders = this.dashboardRecentFolders.map((item) => (item.id === id ? { ...item, visibility } : item));
      this.workspaceItems = this.workspaceItems.map((row) =>
        row.kind === 'folder' && row.folder.id === id ? { kind: 'folder', folder: { ...row.folder, visibility } } : row
      );
      this.sharedItems = this.sharedItems.map((row) =>
        row.kind === 'folder' && row.folder.id === id ? { kind: 'folder', folder: { ...row.folder, visibility } } : row
      );
      if (this.currentFolder?.id === id) {
        this.currentFolder = { ...this.currentFolder, visibility };
      }
      this.breadcrumb = this.breadcrumb.map((item) => (item.id === id ? { ...item, visibility } : item));
      return;
    }
    this.files = this.files.map((item) => (item.id === id ? { ...item, visibility } : item));
    this.dashboardRecentFiles = this.dashboardRecentFiles.map((item) => (item.id === id ? { ...item, visibility } : item));
    this.workspaceItems = this.workspaceItems.map((row) =>
      row.kind === 'file' && row.file.id === id ? { kind: 'file', file: { ...row.file, visibility } } : row
    );
    this.sharedItems = this.sharedItems.map((row) =>
      row.kind === 'file' && row.file.id === id ? { kind: 'file', file: { ...row.file, visibility } } : row
    );
  }

  private syncTargetName(type: 'FILE' | 'FOLDER', id: number, name: string) {
    if (type === 'FOLDER') {
      const renameFolder = (item: ControlDocsFolder) => (item.id === id ? { ...item, name } : item);
      this.rootFolders = this.rootFolders.map(renameFolder);
      this.childFolders = this.childFolders.map(renameFolder);
      this.dashboardRecentFolders = this.dashboardRecentFolders.map(renameFolder);
      this.favoriteFolderItems = this.favoriteFolderItems.map(renameFolder);
      this.workspaceItems = this.workspaceItems.map((row) =>
        row.kind === 'folder' && row.folder.id === id ? { kind: 'folder', folder: { ...row.folder, name } } : row
      );
      this.sharedItems = this.sharedItems.map((row) =>
        row.kind === 'folder' && row.folder.id === id ? { kind: 'folder', folder: { ...row.folder, name } } : row
      );
      this.trashItems = this.trashItems.map((row) =>
        row.kind === 'folder' && row.folder.id === id ? { kind: 'folder', folder: { ...row.folder, name } } : row
      );
      this.breadcrumb = this.breadcrumb.map((item) => (item.id === id ? { ...item, name } : item));
      if (this.currentFolder?.id === id) {
        this.currentFolder = { ...this.currentFolder, name };
      }
      const indexed = this.folderIndex.get(id);
      if (indexed) {
        this.folderIndex.set(id, { ...indexed, name });
      }
      return;
    }

    const renameFile = (item: ControlDocsFile) => (item.id === id ? { ...item, name } : item);
    this.files = this.files.map(renameFile);
    this.dashboardRecentFiles = this.dashboardRecentFiles.map(renameFile);
    this.workspaceItems = this.workspaceItems.map((row) =>
      row.kind === 'file' && row.file.id === id ? { kind: 'file', file: { ...row.file, name } } : row
    );
    this.sharedItems = this.sharedItems.map((row) =>
      row.kind === 'file' && row.file.id === id ? { kind: 'file', file: { ...row.file, name } } : row
    );
    this.trashItems = this.trashItems.map((row) =>
      row.kind === 'file' && row.file.id === id ? { kind: 'file', file: { ...row.file, name } } : row
    );
  }

  private loadShareDataForTarget(type: 'FILE' | 'FOLDER', targetId: number) {
    if (this.tenantId && this.userId) {
      this.shareGenerating = true;
      const sub = this.docs.getShareLinkByTarget(this.tenantId, this.userId, type, targetId).subscribe({
        next: (response) => {
          this.shareGenerating = false;
          if (!response?.id || !response?.access_token) {
            this.shareLinkId = null;
            this.shareLink = null;
            this.shareHasPassword = false;
            this.shareCurrentPasswordValue = null;
            this.shareCurrentExpiresAt = null;
            return;
          }
          this.shareLinkId = response.id;
          this.shareHasPassword = !!response.has_password;
          this.shareIsActive = response.is_active !== false;
          this.shareLink = `${window.location.origin}/docs/share/${response.access_token}`;
          this.shareCurrentPasswordValue = response.current_password || null;
          this.shareCurrentExpiresAt = response.expires_at || null;
          this.shareForm.patchValue({
            password_enabled: response.has_password,
            password: '',
            expires_at: response.expires_at ? String(response.expires_at).slice(0, 10) : ''
          });
        },
        error: () => {
          this.shareGenerating = false;
        }
      });
      this.subscriptions.push(sub);
    }
  }

  closeShareModal() {
    this.shareModalVisible = false;
    this.shareGenerating = false;
    this.shareLinkId = null;
    this.shareHasPassword = false;
    this.shareIsActive = true;
    this.shareCurrentPasswordValue = null;
    this.shareCurrentExpiresAt = null;
  }

  private loadAccessGrants() {
    if (!this.tenantId || !this.userId || !this.detailsTarget) {
      return;
    }
    this.accessGrantsLoading = true;
    const sub = this.docs
      .getAccessGrants(this.tenantId, this.userId, this.detailsTarget.type, this.detailsTarget.id)
      .subscribe({
        next: (response) => {
          this.accessGrantsLoading = false;
          this.accessGrantsEmails = response?.emails || [];
          this.hydrateAccessGrantTags(this.accessGrantsEmails);
        },
        error: () => {
          this.accessGrantsLoading = false;
          this.accessGrantsEmails = [];
          this.accessGrantsInput = '';
          this.accessGrantTags = [];
        }
      });
    this.subscriptions.push(sub);
  }

  saveAccessGrants() {
    if (!this.canManageSharedActions() || !this.tenantId || !this.userId || !this.detailsTarget) {
      return;
    }
    const emails = Array.from(new Set(this.accessGrantTags.map((tag) => tag.email.trim().toLowerCase()).filter((item) => !!item)));
    this.accessGrantsSaving = true;
    const sub = this.docs
      .saveAccessGrants({
        tenant_id: this.tenantId,
        user_id: this.userId,
        target_type: this.detailsTarget.type,
        target_id: this.detailsTarget.id,
        emails
      })
      .subscribe({
        next: (response) => {
          this.accessGrantsSaving = false;
          this.accessGrantsEmails = response?.emails || [];
          this.hydrateAccessGrantTags(this.accessGrantsEmails);
          alert('Permissões internas atualizadas.');
        },
        error: (error) => {
          this.accessGrantsSaving = false;
          const message = error?.error?.error || 'Não foi possível salvar permissões internas.';
          alert(message);
        }
      });
    this.subscriptions.push(sub);
  }

  generateShareLink() {
    if (!this.shareTarget || !this.tenantId || !this.userId) {
      return;
    }
    this.shareGenerating = true;
    const values = this.shareForm.value;
    const request$: any =
      this.shareLinkId
        ? this.docs.updateShareLink(this.shareLinkId, {
            tenant_id: this.tenantId,
            user_id: this.userId,
            password_enabled: !!values.password_enabled,
            password: values.password || undefined,
            expires_at: values.expires_at || null,
            is_active: this.shareIsActive
          })
        : this.docs.createShareLink({
            tenant_id: this.tenantId,
            created_by: this.userId,
            target_type: this.shareTarget.type,
            target_id: this.shareTarget.id,
            password: values.password_enabled ? values.password || undefined : undefined,
            expires_at: values.expires_at || undefined
          });
    const subRef = request$.subscribe({
      next: (response: any) => {
        this.shareGenerating = false;
        const token = response?.access_token;
        if (response?.id) {
          this.shareLinkId = response.id;
        }
        if (token) {
          const base = window.location.origin;
          this.shareLink = `${base}/docs/share/${token}`;
        }
        this.shareHasPassword = !!response?.has_password || (!!values.password_enabled && !!values.password);
        this.sharePassword = values.password || null;
        this.shareCurrentPasswordValue = response?.current_password || this.sharePassword || null;
        this.shareCurrentExpiresAt = values.expires_at || response?.expires_at || null;
        this.shareIsActive = response?.is_active !== false;
        this.shareStep = 'result';
        if (this.currentFolder?.id) {
          this.loadChildFolders(this.currentFolder.id);
          this.loadFiles(this.currentFolder.id);
        } else {
          this.loadRoot();
        }
      },
      error: () => (this.shareGenerating = false)
    });
    this.subscriptions.push(subRef);
  }

  canSubmitShare(): boolean {
    if (this.shareGenerating) {
      return false;
    }
    const values = this.shareForm.value;
    if (this.shareLinkId && !this.shareIsActive) {
      return true;
    }
    if (this.shareLinkId) {
      return true;
    }
    if (!this.shareIsActive) {
      return false;
    }
    return !values.password_enabled || !!values.password;
  }

  copyShareLink() {
    if (!this.shareLink) {
      return;
    }
    navigator.clipboard?.writeText(this.shareLink).then(() => {
      alert('Link copiado para a área de transferência.');
    });
  }

  copySharePassword() {
    if (!this.sharePassword) {
      return;
    }
    navigator.clipboard?.writeText(this.sharePassword).then(() => {
      alert('Senha copiada para a área de transferência.');
    });
  }

  copyCurrentSharePassword() {
    if (!this.shareCurrentPasswordValue) {
      alert('Senha atual não disponível para este link. Defina uma nova senha e salve para registrar e copiar.');
      return;
    }
    navigator.clipboard?.writeText(this.shareCurrentPasswordValue).then(() => {
      alert('Senha atual copiada para a área de transferência.');
    });
  }

  copyShareAll() {
    if (!this.shareLink || !this.sharePassword) {
      return;
    }
    const text = `Link: ${this.shareLink}\nSenha: ${this.sharePassword}`;
    navigator.clipboard?.writeText(text).then(() => {
      alert('Link e senha copiados para a área de transferência.');
    });
  }

  backToShareForm() {
    this.shareStep = 'form';
  }

  private persistViewState() {
    try {
      const state = {
        activeSection: this.activeSection,
        viewMode: this.viewMode,
        currentFolder: this.currentFolder
          ? {
              id: this.currentFolder.id,
              tenant_id: this.currentFolder.tenant_id,
              parent_folder_id: this.currentFolder.parent_folder_id ?? null,
              name: this.currentFolder.name,
              visibility: this.currentFolder.visibility,
              created_at: this.currentFolder.created_at || '',
              created_by: this.currentFolder.created_by
            }
          : null,
        breadcrumb: (this.breadcrumb || []).map((item) => ({
          id: item.id,
          tenant_id: item.tenant_id,
          parent_folder_id: item.parent_folder_id ?? null,
          name: item.name,
          visibility: item.visibility,
          created_at: item.created_at || '',
          created_by: item.created_by
        }))
      };
      localStorage.setItem(this.viewStateKey, JSON.stringify(state));
    } catch {
      // noop
    }
  }

  private restoreViewState() {
    try {
      const raw = localStorage.getItem(this.viewStateKey);
      if (!raw) {
        return;
      }
      const state = JSON.parse(raw);
      if (
        state?.activeSection === 'dashboard'
        || state?.activeSection === 'documents'
        || state?.activeSection === 'workspace'
        || state?.activeSection === 'shared'
        || state?.activeSection === 'trash'
      ) {
        this.activeSection = state.activeSection;
      }
      if (state?.viewMode === 'grid' || state?.viewMode === 'list') {
        this.viewMode = 'list';
      }
      if (state?.currentFolder?.id) {
        this.currentFolder = state.currentFolder;
      }
      if (Array.isArray(state?.breadcrumb)) {
        this.breadcrumb = state.breadcrumb;
        state.breadcrumb.forEach((item: ControlDocsFolder) => {
          if (item?.id) {
            this.folderIndex.set(item.id, item);
          }
        });
      }
    } catch {
      // noop
    }
  }

  private loadDashboardData() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const sub = this.docs.getDashboard(this.tenantId, this.userId).subscribe({
      next: (dashboard) => {
        this.dashboardRecentFolders = dashboard?.recent_folders || [];
        this.dashboardRecentFiles = dashboard?.recent_files || [];
        this.dashboardActivities = dashboard?.recent_activities || [];
      }
    });
    this.subscriptions.push(sub);
  }

  private loadFavoriteFolders() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const sub = this.docs.getFavoriteFolders(this.tenantId, this.userId).subscribe({
      next: (folders) => {
        this.favoriteFolderItems = folders || [];
        this.indexFolders(folders || []);
      },
      error: () => {
        this.favoriteFolderItems = [];
      }
    });
    this.subscriptions.push(sub);
  }

  private loadSectionItems() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const mode: ControlDocsWorkspaceMode = this.activeSection === 'shared' ? 'shared' : 'owned';
    const sub = this.docs.getWorkspaceItems(this.tenantId, this.userId, mode).subscribe({
      next: (response: ControlDocsWorkspaceItems) => {
        const folderRows = (response?.folders || []).map((folder) => ({ kind: 'folder' as const, folder }));
        const fileRows = (response?.files || []).map((file) => ({ kind: 'file' as const, file }));
        const rows = [...folderRows, ...fileRows];
        if (mode === 'shared') {
          this.sharedItems = rows;
        } else {
          this.workspaceItems = rows;
        }
        this.indexFolders(response?.folders || []);
      },
      error: () => {
        if (mode === 'shared') {
          this.sharedItems = [];
        } else {
          this.workspaceItems = [];
        }
      }
    });
    this.subscriptions.push(sub);
  }

  private loadTrashItems(parentId?: number) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const sub = this.docs.getTrashItems(this.tenantId, this.userId, parentId).subscribe({
      next: (response: ControlDocsTrashItems) => {
        const folderRows = (response?.folders || []).map((folder) => ({ kind: 'folder' as const, folder }));
        const fileRows = (response?.files || []).map((file) => ({ kind: 'file' as const, file }));
        this.trashItems = [...folderRows, ...fileRows];
        this.indexFolders(response?.folders || []);
      },
      error: () => {
        this.trashItems = [];
      }
    });
    this.subscriptions.push(sub);
  }

  private logActivity(
    actionType: string,
    targetType: 'FOLDER' | 'FILE' | 'SYSTEM',
    targetId?: number | null,
    targetName?: string | null,
    meta?: Record<string, any>
  ) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const sub = this.docs
      .logActivity({
        tenant_id: this.tenantId,
        user_id: this.userId,
        action_type: actionType,
        target_type: targetType,
        target_id: targetId ?? null,
        target_name: targetName ?? null,
        meta: meta || undefined
      })
      .subscribe({
        next: () => {
          this.loadDashboardData();
        }
      });
    this.subscriptions.push(sub);
  }

  activityLabel(item: ControlDocsActivity) {
    const action = String(item?.action_type || '').toUpperCase();
    switch (action) {
      case 'ACCESS_FOLDER':
        return 'Acessou pasta';
      case 'ACCESS_FILE':
        return 'Acessou arquivo';
      case 'DOWNLOAD_FILE':
        return 'Fez download';
      case 'UPLOAD_FILE':
        return 'Fez upload';
      case 'DELETE_FILE':
        return 'Excluiu arquivo';
      case 'DELETE_FOLDER':
        return 'Excluiu pasta';
      case 'CREATE_FOLDER':
        return 'Criou pasta';
      default:
        return action || 'Atividade';
    }
  }

  get filteredTenantUsers() {
    const term = this.normalizeDisplayText(this.accessSearchTerm || '').trim().toLowerCase();
    const selected = new Set(this.accessGrantTags.map((tag) => tag.email.toLowerCase()));
    const users = (this.tenantUsers || []).filter((user) => Number(user?.active || 0) === 1 || user?.active === undefined || user?.active === null);
    return users
      .filter((user) => {
        const email = String(user?.email || '').toLowerCase();
        if (!email || selected.has(email)) {
          return false;
        }
        if (!term) {
          return true;
        }
        return (
          String(user?.name || '').toLowerCase().includes(term) ||
          email.includes(term)
        );
      })
      .slice(0, 12);
  }

  get showAccessSearchResults() {
    return this.accessSearchOpen && (this.filteredTenantUsers.length > 0 || !!this.accessSearchTerm.trim());
  }

  onAccessSearchFocus(event?: Event) {
    event?.stopPropagation();
    this.accessSearchOpen = true;
  }

  onAccessSearchInput(event?: Event) {
    event?.stopPropagation();
    this.accessSearchOpen = true;
  }

  onAccessSearchConfirm(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const raw = String(this.accessSearchTerm || '').trim();
    if (!raw) {
      return;
    }
    const matched = this.filteredTenantUsers.find(
      (user) =>
        String(user.email || '').toLowerCase() === raw.toLowerCase() ||
        String(user.name || '').toLowerCase() === raw.toLowerCase()
    );
    if (matched) {
      this.addAccessGrantUser(matched);
      return;
    }
    if (this.isValidEmail(raw)) {
      this.addAccessGrantEmail(raw);
    }
  }

  addAccessGrantUser(user: { id: number; name: string; email: string }) {
    const email = String(user?.email || '').trim().toLowerCase();
    if (!email) {
      return;
    }
    const exists = this.accessGrantTags.some((tag) => tag.email.toLowerCase() === email);
    if (!exists) {
      const name = this.normalizeDisplayText(user.name || '').trim();
      const label = name ? `${name} (${email})` : email;
      this.accessGrantTags = [...this.accessGrantTags, { email, label }];
      this.syncAccessGrantInput();
    }
    this.accessSearchTerm = '';
    this.accessSearchOpen = false;
  }

  addAccessGrantEmail(rawEmail: string) {
    const email = String(rawEmail || '').trim().toLowerCase();
    if (!this.isValidEmail(email)) {
      return;
    }
    const exists = this.accessGrantTags.some((tag) => tag.email.toLowerCase() === email);
    if (!exists) {
      this.accessGrantTags = [...this.accessGrantTags, { email, label: email }];
      this.syncAccessGrantInput();
    }
    this.accessSearchTerm = '';
    this.accessSearchOpen = false;
  }

  removeAccessGrantTag(email: string, event?: Event) {
    event?.stopPropagation();
    const normalized = String(email || '').trim().toLowerCase();
    this.accessGrantTags = this.accessGrantTags.filter((tag) => tag.email.toLowerCase() !== normalized);
    this.syncAccessGrantInput();
  }

  private ensureTenantUsersLoaded() {
    if (!this.tenantId || this.usersLoading || this.tenantUsers.length) {
      return;
    }
    this.usersLoading = true;
    const sub = this.userService.getUsersByTenant(this.tenantId).subscribe({
      next: (rows) => {
        this.usersLoading = false;
        this.tenantUsers = (rows || [])
          .map((row: any) => ({
            id: Number(row?.id || 0),
            name: this.normalizeDisplayText(row?.name || ''),
            email: String(row?.email || '').toLowerCase(),
            active: Number(row?.active || 0)
          }))
          .filter((row) => !!row.id && !!row.email);
        if (this.accessGrantsEmails.length) {
          this.hydrateAccessGrantTags(this.accessGrantsEmails);
        }
      },
      error: () => {
        this.usersLoading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  private hydrateAccessGrantTags(emails: string[]) {
    const unique = Array.from(new Set((emails || []).map((item) => String(item || '').trim().toLowerCase()).filter((item) => !!item)));
    const tags = unique.map((email) => {
      const matched = this.tenantUsers.find((user) => user.email.toLowerCase() === email);
      const label = matched?.name ? `${matched.name} (${email})` : email;
      return { email, label };
    });
    this.accessGrantTags = tags;
    this.syncAccessGrantInput();
  }

  private syncAccessGrantInput() {
    this.accessGrantsInput = this.accessGrantTags.map((item) => item.email).join(', ');
  }

  private isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

}
